<template>
  <main>
    <section class="section banner page-title-banner">
      <div class="shell">
        <header class="page-head compact">
          <h1>Request service</h1>
          <p>Submit your work request and we will contact you to confirm scope, timing, and next steps.</p>
        </header>
      </div>
    </section>

    <section class="section form-wrap">
      <div class="shell request-layout">
        <aside class="card request-visual">
          <img src="/assets/local/service-request.jpg" alt="Centerfire shop and mobile service truck" />
          <div class="request-visual-copy">
            <p class="eyebrow">Local Service</p>
            <h2>Serving Bandera County and nearby areas</h2>
            <p>Mobile support for equipment, fleet, and on-site repair requests.</p>
          </div>
        </aside>

        <div class="card panel form-card">
          <div v-if="submitted" class="notice notice-success" role="status">
            <h2>Request received</h2>
            <p>Thanks, {{ submittedName }}. We have your request and will reach out to confirm scope and timing.</p>
            <p>Need us sooner? Call <a :href="phoneHref">{{ brand.phone }}</a>.</p>
            <button type="button" class="btn btn-ghost" @click="startNewRequest">Submit another request</button>
          </div>

          <form v-else novalidate @submit.prevent="handleSubmit">
            <p class="form-lead">All fields are required. We typically respond within one business day.</p>

            <div class="field">
              <label for="name">Name</label>
              <input id="name" v-model="form.name" type="text" autocomplete="name" :aria-invalid="!!errors.name" />
              <p v-if="errors.name" class="field-error">{{ errors.name }}</p>
            </div>

            <div class="field-row">
              <div class="field">
                <label for="email">Email</label>
                <input id="email" v-model="form.email" type="email" autocomplete="email" :aria-invalid="!!errors.email" />
                <p v-if="errors.email" class="field-error">{{ errors.email }}</p>
              </div>

              <div class="field">
                <label for="phone">Phone</label>
                <input id="phone" v-model="form.phone" type="tel" autocomplete="tel" :aria-invalid="!!errors.phone" />
                <p v-if="errors.phone" class="field-error">{{ errors.phone }}</p>
              </div>
            </div>

            <div class="field">
              <label for="location">Service location</label>
              <input
                id="location"
                v-model="form.location"
                type="text"
                autocomplete="street-address"
                placeholder="Address or nearest crossroads"
                :aria-invalid="!!errors.location"
              />
              <p v-if="errors.location" class="field-error">{{ errors.location }}</p>
            </div>

            <div class="field">
              <label for="serviceType">Service type</label>
              <select
                id="serviceType"
                v-model="form.serviceType"
                :class="{ 'is-placeholder': !form.serviceType }"
                :aria-invalid="!!errors.serviceType"
              >
                <option value="" disabled>Select a service</option>
                <option v-for="option in serviceTypes" :key="option" :value="option">{{ option }}</option>
              </select>
              <p v-if="errors.serviceType" class="field-error">{{ errors.serviceType }}</p>
            </div>

            <div class="field">
              <label for="details">What do you need done?</label>
              <textarea
                id="details"
                v-model="form.details"
                rows="6"
                placeholder="Equipment or vehicle, the problem you are seeing, and any timing constraints."
                :aria-invalid="!!errors.details"
              ></textarea>
              <p v-if="errors.details" class="field-error">{{ errors.details }}</p>
            </div>

            <!-- Honeypot: hidden from people, irresistible to bots. -->
            <div class="honeypot" aria-hidden="true">
              <label for="company">Company</label>
              <input id="company" v-model="form.company" type="text" tabindex="-1" autocomplete="off" />
            </div>

            <p v-if="submitError" class="notice notice-error" role="alert">{{ submitError }}</p>

            <button type="submit" class="btn btn-primary submit" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner" aria-hidden="true"></span>
              {{ isSubmitting ? 'Sending...' : 'Send request' }}
            </button>
          </form>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { brand, services } from '@/data/content';
import { findListing } from '@/data/machinesForSale';
import { loadMachineTagsResult } from '@/data/maintainedUnits';

const ENDPOINT = '/api/service-request';

// Not services - these exist so an enquiry raised from the sale section arrives already
// labelled as one, rather than as "Other / not sure" with the reason buried in the details.
// Buying and selling are kept apart on purpose: they are different conversations, and a
// buyer's enquiry filed as "selling" is worse than no label at all.
//
// These strings must appear verbatim in SERVICE_TYPES in functions/index.js. The server
// rejects anything not on its allowlist.
// Must match the service title in content.js; the select rejects anything not in that list.
const MANAGED_MAINTENANCE = 'Managed Maintenance Systems';

const SALE_INTENT = {
  sell: 'Interest in selling a machine',
  buy: 'Interest in a machine listed for sale',
};

function emptyForm() {
  return {
    name: '',
    email: '',
    phone: '',
    location: '',
    serviceType: '',
    details: '',
    company: '',
  };
}

export default {
  name: 'ServiceRequestView',
  data() {
    return {
      brand,
      serviceTypes: [
        ...services.map((service) => service.title),
        SALE_INTENT.sell,
        SALE_INTENT.buy,
        'Other / not sure',
      ],
      form: emptyForm(),
      errors: {},
      isSubmitting: false,
      submitError: '',
      submitted: false,
      submittedName: '',
    };
  },
  computed: {
    phoneHref() {
      return `tel:${brand.phone.replace(/\D/g, '')}`;
    },
  },
  async created() {
    await this.applySaleIntent();
    await this.applyMachineIntent();
  },
  methods: {
    // Both doors from the sale section land here and both pre-select the selling option
    // (Daniel 2026-08-15). What tells them apart is the details line: a buyer's names the
    // machine they came in from, a seller's does not.
    //
    // Nothing a visitor puts in the URL is echoed back into the form - the slug only
    // SELECTS a listing, and the wording comes out of our own record.
    async applySaleIntent() {
      // Seller - came from "Have something you want listed here?" on the directory. Read
      // first because it costs no round trip, and a seller should not wait on a lookup that
      // was only ever going to miss.
      const wantsToSell = this.$route.query.intent === 'sell';
      // Buyer - came from "Ask about this machine" on a listing. A fetch now rather than a
      // compiled-in array, so a listing we cannot reach still pre-selects the sale option; it
      // just cannot name the machine, which is the honest fallback.
      const listing = this.$route.query.about
        ? (await findListing(this.$route.query.about)).listing
        : null;
      if (!listing && !wantsToSell) return;

      this.form.serviceType = SALE_INTENT.sell;
      this.form.details = listing
        ? `I am interested in the ${listing.title} listed for sale. `
        : 'I have a machine I would like listed for sale. ';
    },
    /**
     * Someone scanned a tag, read the plans page, and asked for a quote on that machine.
     *
     * Same discipline as applySaleIntent: nothing from the URL is echoed back. The key only
     * SELECTS a machine out of the public directory, and the wording is built from our own
     * record. A key that matches nothing — an untagged machine's private token, a typo — still
     * pre-selects managed maintenance, it just cannot name the unit.
     */
    async applyMachineIntent() {
      const key = String(this.$route.query.machine || '').trim();
      if (!key || this.form.details) return;
      this.form.serviceType = MANAGED_MAINTENANCE;
      const { tags } = await loadMachineTagsResult();
      const machine = tags.find((tag) => tag.lookupNumber && tag.lookupNumber === key.toUpperCase());
      this.form.details = machine
        ? `I would like a quote for managed maintenance on CFM# ${machine.lookupNumber}${machine.label ? ` (${machine.label})` : ''}. `
        : 'I would like a quote for managed maintenance on the machine whose tag I scanned. ';
    },
    validate() {
      const errors = {};

      if (this.form.name.trim().length < 2) {
        errors.name = 'Please enter your name.';
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.form.email.trim())) {
        errors.email = 'Please enter a valid email address.';
      }

      if (this.form.phone.replace(/\D/g, '').length < 10) {
        errors.phone = 'Please enter a phone number we can reach you at.';
      }

      if (this.form.location.trim().length < 3) {
        errors.location = 'Tell us where the work is.';
      }

      if (!this.serviceTypes.includes(this.form.serviceType)) {
        errors.serviceType = 'Select the service you need.';
      }

      if (this.form.details.trim().length < 10) {
        errors.details = 'Add a short description of the work.';
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    focusFirstError() {
      const firstField = ['name', 'email', 'phone', 'location', 'serviceType', 'details'].find(
        (field) => this.errors[field],
      );

      if (firstField) {
        const element = document.getElementById(firstField);
        if (element) {
          element.focus();
        }
      }
    },
    async handleSubmit() {
      this.submitError = '';

      if (!this.validate()) {
        this.$nextTick(() => this.focusFirstError());
        return;
      }

      this.isSubmitting = true;

      try {
        const response = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.form),
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Something went wrong sending your request.');
        }

        this.submittedName = this.form.name.trim();
        this.submitted = true;
        this.form = emptyForm();
        this.errors = {};
      } catch (error) {
        this.submitError = `${error.message} You can also call us at ${brand.phone}.`;
      } finally {
        this.isSubmitting = false;
      }
    },
    startNewRequest() {
      this.submitted = false;
      this.submitError = '';
      this.submittedName = '';
    },
  },
};
</script>

<style scoped>
.banner {
  background: var(--black);
  border-bottom: 1px solid var(--orange);
}

.page-title-banner {
  padding-top: clamp(10rem, 15vw, 12rem);
}

.compact {
  margin-bottom: 0;
}

.form-wrap {
  min-height: 52vh;
}

.request-layout {
  display: grid;
  grid-template-columns: minmax(300px, 0.85fr) minmax(0, 1.15fr);
  gap: 1rem;
  align-items: start;
}

.request-visual {
  overflow: hidden;
}

.request-visual img {
  width: 100%;
  min-height: 300px;
  max-height: 380px;
  object-fit: cover;
}

.request-visual-copy {
  padding: 1rem;
  display: grid;
  gap: 0.6rem;
}

.request-visual-copy h2 {
  font-size: clamp(1.25rem, 2.1vw, 1.7rem);
}

.request-visual-copy p:last-child {
  color: var(--white);
}

.form-card {
  min-height: 380px;
  padding: 1.4rem;
}

.form-lead {
  color: var(--white);
  margin-bottom: 1.2rem;
  font-size: 0.95rem;
}

.field {
  display: grid;
  gap: 0.35rem;
  margin-bottom: 1rem;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

label {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

input,
select,
textarea {
  width: 100%;
  padding: 0.7rem 0.8rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--white);
  background: var(--white);
  color: var(--black);
  font: inherit;
  font-size: 1rem;
}

textarea {
  resize: vertical;
  min-height: 130px;
  line-height: 1.6;
}

select {
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--orange) 50%),
    linear-gradient(135deg, var(--orange) 50%, transparent 50%);
  background-position: calc(100% - 18px) 52%, calc(100% - 13px) 52%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 2.2rem;
}

select option {
  background: var(--white);
  color: var(--black);
}

/* Matches the ::placeholder treatment on the text fields. */
select.is-placeholder {
  color: var(--orange);
}

/* Placeholders are orange rather than a dimmed grey — the palette has no grey,
   and orange still reads as "not yet filled in" next to black typed text. */
input::placeholder,
textarea::placeholder {
  color: var(--orange);
  opacity: 1;
}

input:focus-visible,
select:focus-visible,
textarea:focus-visible {
  outline: 2px solid var(--orange);
  outline-offset: 1px;
  border-color: transparent;
}

/* An orange rule around the white field — visible against both the white fill
   and the black panel behind it. */
[aria-invalid='true'] {
  border: 2px solid var(--orange);
}

.field-error {
  color: var(--orange);
  font-size: 0.86rem;
  font-weight: 700;
}

.honeypot {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.notice {
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid var(--line);
}

.notice-error {
  margin-bottom: 1rem;
  border-color: var(--orange);
  background: var(--orange);
  color: var(--black);
  font-weight: 700;
}

.notice-success {
  display: grid;
  gap: 0.7rem;
  justify-items: start;
  border-color: var(--orange);
  background: var(--black);
}

.notice-success a {
  color: var(--orange);
  font-weight: 700;
}

.notice-success .btn {
  margin-top: 0.4rem;
}

.submit {
  gap: 0.6rem;
  width: 100%;
  margin-top: 0.4rem;
}

/* No opacity here — fading the fill would blend a colour outside the palette.
   The sending state inverts to the same treatment as :hover instead. */
.submit:disabled {
  background: var(--black);
  color: var(--orange);
  cursor: progress;
  transform: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 960px) {
  .request-layout {
    grid-template-columns: 1fr;
  }

  .request-visual img {
    min-height: 220px;
    max-height: 300px;
  }
}

@media (max-width: 560px) {
  .field-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
